// Project-template helpers — Story 7.
//
// Read-side caches the template list in a module-level signal so the
// NewProjectModal can open instantly on subsequent opens. createProjectFromTemplate
// is a thin POST helper that returns the materialized Project.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { Project, ProjectTemplateCreate, ProjectTemplateItem } from "@juuplan/shared";
import { api } from "../lib/api";
import {
  createTemplate as apiCreateTemplate,
  deleteTemplate as apiDeleteTemplate,
  saveProjectAsTemplate as apiSaveProjectAsTemplate,
} from "../lib/api";
import { refreshProjects } from "./projects";

export interface TemplatesState {
  loading: boolean;
  items: ProjectTemplateItem[];
  error: string | null;
}

const initial: TemplatesState = { loading: false, items: [], error: null };
const templatesSignal: Signal<TemplatesState> = signal(initial);
let inFlight: Promise<void> | null = null;
let loadedOnce = false;

export async function fetchTemplates(force = false): Promise<ProjectTemplateItem[]> {
  if (!force && loadedOnce && !templatesSignal.value.loading) {
    return templatesSignal.value.items;
  }
  if (inFlight) {
    await inFlight;
    return templatesSignal.value.items;
  }
  templatesSignal.value = { ...templatesSignal.value, loading: true, error: null };
  inFlight = (async () => {
    try {
      const items = await api<ProjectTemplateItem[]>("/api/templates");
      templatesSignal.value = { loading: false, items, error: null };
      loadedOnce = true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      templatesSignal.value = { loading: false, items: [], error: message };
    } finally {
      inFlight = null;
    }
  })();
  await inFlight;
  return templatesSignal.value.items;
}

/** Hook variant — triggers a load on mount and exposes the live state. */
export function useTemplates(): TemplatesState {
  useEffect(() => {
    if (!loadedOnce && !inFlight) {
      void fetchTemplates();
    }
  }, []);
  return templatesSignal.value;
}

export interface CreateFromTemplateInput {
  template_id: number;
  name: string;
  owner_id?: number;
  start_date?: string;
}

export async function createProjectFromTemplate(
  input: CreateFromTemplateInput
): Promise<Project> {
  const created = await api<Project>("/api/projects/from-template", {
    method: "POST",
    body: input,
  });
  await refreshProjects();
  return created;
}

/** Create a new template from a payload. Refreshes the templates list on success. */
export async function saveTemplate(body: ProjectTemplateCreate): Promise<ProjectTemplateItem> {
  const item = await apiCreateTemplate(body);
  await fetchTemplates(true);
  return item;
}

/** Snapshot an existing project's current phases + tasks into a new template
 *  (#76). The server converts dates to relative day-offsets so the template
 *  rolls forward correctly from any future start_date. Refreshes the
 *  templates list on success. */
export async function saveProjectAsTemplate(
  projectId: number,
  body: { name: string; description?: string | null }
): Promise<ProjectTemplateItem> {
  const item = await apiSaveProjectAsTemplate(projectId, body);
  await fetchTemplates(true);
  return item;
}

/** Delete a template by id. Refreshes the templates list on success. */
export async function removeTemplate(id: number): Promise<void> {
  await apiDeleteTemplate(id);
  await fetchTemplates(true);
}
